/**
 * Nav icon-set preference — core (framework-free) logic, same shape and testing story as the
 * theme preference in `theme.ts` (split out so it can be unit-tested without a DOM).
 *
 * Owner call, 2026-08-01: "the logos we have are super dumb" turned out to mean the *nav
 * icons* — the emoji strings modules register in their `nav.icon` field (📅 📝 📷 📖 ☀️ ⚙️).
 * Rather than pick a replacement blind, the chrome now resolves each entry's icon through one
 * of three sets and lets the household try them on.
 *
 * The registry contract is deliberately untouched: a module still declares a plain emoji
 * string in `nav.icon`, and that string is what the `'emoji'` set renders and what any
 * *unknown* module id falls back to under the other two sets. A future module therefore
 * degrades gracefully (its emoji shows up) instead of rendering a blank tab, and it can opt
 * into the drawn sets later by adding itself to `apps/web/src/chrome/icons/`.
 */

export const ICON_SET_STORAGE_KEY = 'awwyeah:icons';

export const ICON_SETS = [
  {
    id: 'line',
    label: 'Line',
    description: 'Clean single-weight outlines. The grown-up option.',
  },
  {
    id: 'rounded',
    label: 'Rounded',
    description: 'Chunky filled shapes with soft corners. Friendly.',
  },
  {
    id: 'emoji',
    label: 'Emoji',
    description: 'The original emoji tabs.',
  },
] as const satisfies readonly { id: string; label: string; description: string }[];

export type IconSetId = (typeof ICON_SETS)[number]['id'];

/** What a fresh device gets. Not `'emoji'` — that's the look the owner asked us to move off,
 * kept only as an explicit opt-in. */
export const DEFAULT_ICON_SET: IconSetId = 'line';

export function isIconSetId(value: string | null | undefined): value is IconSetId {
  return value != null && ICON_SETS.some((set) => set.id === value);
}

/** Minimal `Storage` shape we depend on, so tests can pass a fake instead of real localStorage. */
export type IconSetStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function readStoredIconSet(storage: IconSetStorage): IconSetId {
  let raw: string | null = null;
  try {
    raw = storage.getItem(ICON_SET_STORAGE_KEY);
  } catch {
    // Storage can throw (private browsing, quota) — fall back silently.
    return DEFAULT_ICON_SET;
  }
  return isIconSetId(raw) ? raw : DEFAULT_ICON_SET;
}

export function writeStoredIconSet(storage: IconSetStorage, set: IconSetId): void {
  try {
    storage.setItem(ICON_SET_STORAGE_KEY, set);
  } catch {
    // Best-effort persistence; a failed write just means it reverts to the default next launch.
  }
}
